import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  useTheme,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  Description,
  Security,
  Policy,
  Payment,
  Cookie,
  ArrowForward,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

const LegalIndex: React.FC = () => {
  const theme = useTheme();

  const legalDocuments = [
    {
      icon: <Description />,
      title: 'Terms of Service',
      description: 'Our terms and conditions for using Tier One Content',
      path: '/legal/terms',
      updated: 'January 6, 2026',
    },
    {
      icon: <Security />,
      title: 'Privacy Policy',
      description: 'How we collect, use, and protect your information',
      path: '/legal/privacy',
      updated: 'January 6, 2026',
    },
    {
      icon: <Policy />,
      title: 'Acceptable Use Policy',
      description: 'Guidelines for appropriate use of our service',
      path: '/legal/acceptable-use',
      updated: 'January 6, 2026',
    },
    {
      icon: <Payment />,
      title: 'Subscription & Billing Terms',
      description: 'Payment, renewal, and cancellation policies',
      path: '/legal/billing',
      updated: 'January 6, 2026',
    },
    {
      icon: <Cookie />,
      title: 'Cookie Policy',
      description: 'How we use cookies and tracking technologies',
      path: '/legal/cookies',
      updated: 'January 6, 2026',
    },
  ];

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}15 100%)`,
          pt: { xs: 6, md: 10 },
          pb: { xs: 6, md: 10 },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 800,
                mb: 3,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Legal & Policies
            </Typography>
            <Typography variant="h5" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
              Transparency and trust are at the core of everything we do. Review our legal documents
              and policies that govern your use of Tier One Content.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Legal Documents Grid */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={4}>
          {legalDocuments.map((doc) => (
            <Grid item xs={12} md={6} key={doc.title}>
              <Card
                component={Link}
                to={doc.path}
                sx={{
                  height: '100%',
                  textDecoration: 'none',
                  transition: 'all 0.2s',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        backgroundColor: `${theme.palette.primary.main}15`,
                        color: 'primary.main',
                        mr: 2,
                      }}
                    >
                      {doc.icon}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                        {doc.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {doc.description}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Last updated: {doc.updated}
                      </Typography>
                    </Box>
                    <ArrowForward color="action" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Additional Information */}
      <Box sx={{ backgroundColor: 'background.default', py: 8 }}>
        <Container maxWidth="md">
          <Typography variant="h4" sx={{ mb: 4, fontWeight: 600, textAlign: 'center' }}>
            Your Rights & Our Commitments
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Your Data Rights
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="Access your personal data anytime" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Request correction or deletion of your data" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Export your data in common formats" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Opt-out of non-essential data processing" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Control cookie preferences" />
                </ListItem>
              </List>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Our Commitments
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="Never sell your personal data" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Encrypt all data in transit and at rest" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Regular security audits and compliance checks" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Transparent notification of policy changes" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="GDPR and CCPA compliant" />
                </ListItem>
              </List>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Contact Section */}
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
          Questions About Our Policies?
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          We're here to help clarify any questions about our legal documents and policies.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <Typography variant="body1">
            <strong>Legal Inquiries:</strong> legal@tieronecontent.com
          </Typography>
          <Typography variant="body1">
            <strong>Privacy Questions:</strong> privacy@tieronecontent.com
          </Typography>
          <Typography variant="body1">
            <strong>General Support:</strong> support@tieronecontent.com
          </Typography>
        </Box>
      </Container>
    </>
  );
};

export default LegalIndex;